import { useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";
import {
	EventRequirement,
	RequirementField,
	RequirementFieldType,
} from "../types/event";

/**
 * The requirements catalogue, from the shared Dictionary collection.
 *
 * `dictionary/{name}` is the generic lookup every reference list in this app
 * uses — GroupTypes comes from the same place — so adding a ninth requirement
 * is a data change rather than a release.
 *
 * `dataonly=true` returns the `Data` array directly. Without it the endpoint
 * answers `[wholeDocument]` and the caller has to dig `res[0].Data` out, which
 * is what useMyEditorGroups does for GroupTypes.
 *
 * Entries are read in both casings. The Dictionary collection is not
 * consistent about this — GroupTypes is PascalCase, EventRequirements was
 * seeded lowercase, TodoTips is plain strings — so accepting either is the
 * difference between a re-seed in the other style rendering and it silently
 * emptying the tab. Everything is mapped to the app's own shape here, so the
 * DB's field names do not spread through the components.
 *
 * `Code` and `Order` are read too: they are what pymble-apis's catalogue admin
 * (`PUT /event/admin/requirements/{code}`) writes, and an entry saved through it
 * would otherwise lose its id here and drop out of the grid.
 */

const ENDPOINT = "dictionary/EventRequirements?dataonly=true";

interface RequirementEntry {
	id?: string;
	label?: string;
	value?: string;
	description?: string;
	colour?: string;
	icon?: string;
	sort?: number;
	active?: boolean;
	fields?: unknown;
	LocalId?: string;
	Code?: string;
	Name?: string;
	Description?: string;
	Colour?: string;
	Icon?: string;
	Sort?: number;
	Order?: number;
	Active?: boolean;
	Fields?: unknown;
}

const FIELD_TYPES: RequirementFieldType[] = [
	"text",
	"paragraph",
	"select",
	"datetime",
	"info",
];

/** The two casings again, one level down. */
type FieldEntry = Record<string, unknown>;

const pick = (e: FieldEntry, lower: string, upper: string): unknown =>
	e[lower] ?? e[upper];

const str = (value: unknown): string =>
	typeof value === "string" ? value.trim() : "";

/**
 * One form field from the dictionary, or null if it cannot be shown.
 *
 * Forgiving about what it can repair and strict about what it cannot: an
 * unknown type falls back to a text box (a question still gets asked), but a
 * question with no id is dropped, because its answer would have nowhere to be
 * stored. A select with no options is dropped for the same reason.
 *
 * A link is kept only if it is http(s). The dictionary is edited by people,
 * and a `javascript:` URL in an anchor is a script.
 */
function toField(raw: unknown): RequirementField | null {
	if (!raw || typeof raw !== "object") return null;
	const e = raw as FieldEntry;
	const typeName = str(pick(e, "type", "Type")).toLowerCase();
	const type = (FIELD_TYPES as string[]).includes(typeName)
		? (typeName as RequirementFieldType)
		: "text";
	const label = str(pick(e, "label", "Label"));
	const id = str(pick(e, "id", "Id"));
	const text = str(pick(e, "text", "Text"));
	const optionsRaw = pick(e, "options", "Options");
	const options = Array.isArray(optionsRaw)
		? optionsRaw.map(str).filter(Boolean)
		: [];
	const url = str(pick(e, "url", "Url"));

	if (type === "info") {
		if (!text && !label) return null;
	} else if (!id || !label || (type === "select" && options.length === 0)) {
		return null;
	}

	const showIfRaw = pick(e, "showIf", "ShowIf");
	const showIfEntry =
		showIfRaw && typeof showIfRaw === "object" ? (showIfRaw as FieldEntry) : null;
	const showIfId = showIfEntry ? str(pick(showIfEntry, "id", "Id")) : "";
	const showIfEquals = showIfEntry ? str(pick(showIfEntry, "equals", "Equals")) : "";

	return {
		id: id || `info-${label || text}`.slice(0, 64),
		label,
		type,
		required: type !== "info" && pick(e, "required", "Required") === true,
		options,
		hint: str(pick(e, "hint", "Hint")),
		text,
		url: /^https?:\/\//i.test(url) ? url : "",
		linkLabel: str(pick(e, "linkLabel", "LinkLabel")),
		tone: str(pick(e, "tone", "Tone")) === "warning" ? "warning" : "default",
		// Half a condition is no condition: both keys or the field is always shown.
		showIf:
			showIfId && showIfEquals ? { id: showIfId, equals: showIfEquals } : null,
	};
}

/** What a `ShowIf` may depend on: answers held as the same plain string the
 *  dictionary's `Equals` is written in. A `datetime` answer is stored as an ISO
 *  instant but drafted as local `YYYY-MM-DDTHH:mm`, so no `Equals` could match
 *  it reliably. */
const CONDITION_PARENTS: RequirementFieldType[] = ["text", "paragraph", "select"];

function toFields(raw: unknown): RequirementField[] {
	if (!Array.isArray(raw)) return [];
	const seen = new Set<string>();
	const fields = raw
		.map(toField)
		.filter((f): f is RequirementField => {
			// Two questions under one id would share an answer.
			if (!f || seen.has(f.id)) return false;
			seen.add(f.id);
			return true;
		});
	/* A condition on a field that is not an earlier text, paragraph or select
	   is dropped, so the question is always shown rather than hidden forever
	   behind a parent that can never match. */
	const earlier = new Map<string, RequirementFieldType>();
	return fields.map((f) => {
		const parent = f.showIf ? earlier.get(f.showIf.id) : undefined;
		const ok = parent !== undefined && CONDITION_PARENTS.includes(parent);
		earlier.set(f.id, f.type);
		return f.showIf && !ok ? { ...f, showIf: null } : f;
	});
}

export function useEventRequirements() {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const query = useQuery({
		queryKey: ["dictionary", "EventRequirements"],
		queryFn: async (): Promise<EventRequirement[]> => {
			const res = await callPymbleApi(instance, "GET", ENDPOINT);
			if (!Array.isArray(res)) return [];

			return (res as RequirementEntry[])
				.filter((e) => e && typeof e === "object")
				.map((e) => {
					const id = String(e.id ?? e.LocalId ?? e.Code ?? "");
					const sort = e.sort ?? e.Sort ?? e.Order;
					return {
						id,
						// `label` is the editor's lowercase spelling (pymble-apis maps it to
						// `Name`); `value` is the older seed's. Missing all three shows the id.
						label: String(e.label ?? e.value ?? e.Name ?? id),
						description: String(e.description ?? e.Description ?? ""),
						colour: String(e.colour ?? e.Colour ?? ""),
						icon: String(e.icon ?? e.Icon ?? ""),
						sort: typeof sort === "number" ? sort : 0,
						fields: toFields(e.fields ?? e.Fields),
						// A retired requirement is kept, not deleted, so historical
						// events still render it — but it is not offered for new
						// ones. Absent means active: an entry seeded without the
						// flag should show, not vanish.
						active: (e.active ?? e.Active) !== false,
					};
				})
				.filter((e) => e.id && e.active)
				.map(({ active: _active, ...req }) => req)
				.sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
		},
		enabled: isAuthenticated,
	});

	return {
		requirements: useMemo(() => query.data ?? [], [query.data]),
		requirementsLoading: query.isLoading,
		requirementsError: !!query.error,
	};
}
